/**
 * Car movement: continuous in subpixels, grid-aligned at decisions.
 *
 * Decisions happen at tile centers (offset == HALF_TILE on both axes).
 *  - If desired direction is set and that neighbor is corridor: turn that way.
 *  - Else if current direction leads into a wall: stop.
 * Reverse is a special case: a 180° flip is allowed any time, not just at
 * centers, because corridor-traversal is symmetric and arcade-feel demands it.
 *
 * Speeds that divide HALF_TILE land exactly on each tile center every tick.
 * Speeds that don't (e.g. 24) would naturally overshoot; the movement phase
 * clamps to the next tile center along the active axis whenever a step would
 * cross it, then resumes at full speed next tick. Net effect: ~3% slower
 * effective speed at 24, but cars still land precisely on centers and decision
 * frames are never skipped.
 */

import { type Car, type Direction, type InputFrame } from './types.ts';
import { type Maze, isPassable } from './maze.ts';
import { HALF_TILE, SUBPIXELS_PER_TILE, offsetInTile, tileCenter, toTile } from './fixed.ts';
import { dirVec, isReverse } from './direction.ts';

/**
 * Direction the car will commit to this tick — applying the same reverse and
 * tile-center turn rules as `stepCar`, but stopping BEFORE the dead-end check.
 * Returns the direction that stepCar would attempt to advance in (which may
 * be blocked); returns null only when the car would genuinely make no progress
 * (stopped with no usable input, mid-tile with dir already null).
 *
 * Used by callers that need to know "where is the player about to go" — e.g.
 * the boulder-impact death check, which must respect this tick's input so a
 * player who reverses or turns away from a boulder at the last moment isn't
 * killed for the previous direction.
 */
export function committedDirection(car: Car, input: InputFrame, maze: Maze): Direction | null {
  const desired = input.dir;
  let dir = car.dir;
  if (desired !== null && dir !== null && isReverse(desired, dir)) dir = desired;
  if (!atTileCenter({ x: car.x, y: car.y, dir })) return dir;
  const tx = toTile(car.x);
  const ty = toTile(car.y);
  if (desired !== null) {
    const dv = dirVec(desired);
    if (isPassable(maze, tx + dv.dx, ty + dv.dy)) dir = desired;
  }
  return dir;
}

export function spawnCar(spawnX: number, spawnY: number, dir: Direction | null = null): Car {
  return {
    x: tileCenter(spawnX),
    y: tileCenter(spawnY),
    dir,
  };
}

/** True iff both axes are at the half-tile mark — the only place we may pick a new heading. */
export function atTileCenter(car: Car): boolean {
  return offsetInTile(car.x) === HALF_TILE && offsetInTile(car.y) === HALF_TILE;
}

/**
 * Advance one car by `speed` subpixels for one tick.
 *
 * Pure: returns a new Car; never mutates the input.
 */
export function stepCar(car: Car, input: InputFrame, maze: Maze, speed: number): Car {
  const desired = input.dir;
  let { x, y, dir } = car;

  // 1. Reverse is allowed any time (180° flip).
  if (desired !== null && dir !== null && isReverse(desired, dir)) {
    dir = desired;
  }

  // 2. Tile-center decision: pick desired turn or check for dead-end.
  if (atTileCenter({ x, y, dir })) {
    const tx = toTile(x);
    const ty = toTile(y);

    if (desired !== null) {
      const dv = dirVec(desired);
      if (isPassable(maze, tx + dv.dx, ty + dv.dy)) {
        dir = desired;
      }
    }

    if (dir !== null) {
      const cv = dirVec(dir);
      if (!isPassable(maze, tx + cv.dx, ty + cv.dy)) {
        // Dead end / boulder: stop at the tile center.
        dir = null;
      }
    } else if (desired !== null) {
      // Stopped, with input pressed: try to start.
      const dv = dirVec(desired);
      if (isPassable(maze, tx + dv.dx, ty + dv.dy)) {
        dir = desired;
      }
    }
  }

  // 3. Movement phase. Clamp at the next tile center on the active axis if
  // `speed` would overshoot. Cars therefore land exactly on centers even at
  // speeds that don't divide HALF_TILE (e.g. 24).
  if (dir !== null) {
    const v = dirVec(dir);
    let dx = v.dx * speed;
    let dy = v.dy * speed;
    if (v.dx !== 0) {
      const dist = distToNextCenter(x, v.dx);
      if (Math.abs(dx) > dist) dx = v.dx * dist;
    }
    if (v.dy !== 0) {
      const dist = distToNextCenter(y, v.dy);
      if (Math.abs(dy) > dist) dy = v.dy * dist;
    }
    x += dx;
    y += dy;
  }

  return { x, y, dir };
}

// AIDEV-NOTE: "next center" means the next one in the direction of motion.
// If we're already on a center (offset == HALF_TILE), the next is one full
// tile away; otherwise it's the partial distance to HALF_TILE within the
// current or adjacent tile.
function distToNextCenter(pos: number, sign: number): number {
  const off = offsetInTile(pos);
  if (sign > 0) {
    if (off === HALF_TILE) return SUBPIXELS_PER_TILE;
    return off < HALF_TILE ? HALF_TILE - off : SUBPIXELS_PER_TILE + HALF_TILE - off;
  }
  if (off === HALF_TILE) return SUBPIXELS_PER_TILE;
  return off > HALF_TILE ? off - HALF_TILE : SUBPIXELS_PER_TILE + off - HALF_TILE;
}
